/**
 * Story 057 — Turbines list page (`/turbines`).
 *
 * Mirrors `SitesPage` / `TurbineModelsPage` shape. Differences:
 *   - Columns: Turbine Code · Internal Park Name · Identifier · Type
 *     · Park · Edit (pinned).
 *   - Default sort: assetCode:asc (server-side).
 *   - Toolbar row 1: Search + Type (multi) + Park (multi) +
 *     Show-inactive switch.
 *   - Add button visible only for the mutate-role set
 *     (`tenant_admin | project_manager | back_office`).
 *   - Column-visibility key: `aero.turbines.columns`.
 *   - Park-cell drill-down opens the Story 054 Site detail modal
 *     in-place over `/turbines` (AC24).
 *
 * Stories / specs:
 *   - Story 057 (`docs/stories/web/057-turbines-management.md`)
 *   - UX brief (`docs/ux/057-turbines-management.md`)
 *   - Backend specs 057–062
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import type { GridColDef, GridSortModel } from "@mui/x-data-grid";
import {
  AsyncTypeaheadChipFilter,
  ColumnVisibilityButton,
  DataPageFooter,
  DataPageShell,
  DataPageToolbar,
  DataTable,
  DensityToggleButton,
  SearchField,
  TypeaheadChipFilter,
  sidebar as sidebarTokens,
  typography,
  useCanEditResourceFor,
  type ColumnDescriptor,
  type Density,
} from "@aeriosiq/ui";
import { useSearchParams } from "react-router-dom";
import { ToastProvider } from "../account/ToastContext.js";
import {
  useAssetModelsForTypeQuery,
  useTurbinesListQuery,
  type Turbine,
} from "./queries/index.js";
import {
  useAllSitesQuery,
  useSitesSearchQuery,
} from "../sites/queries/index.js";
import { InactiveBadge } from "../sites/shared/OffshoreChip.js";
import { EditSiteDialog } from "../sites/modal-edit/EditSiteDialog.js";
import { EditTurbineModelDialog } from "../turbine-models/modal-edit/EditTurbineModelDialog.js";
import { AddTurbineDialog } from "./modal-add/AddTurbineDialog.js";
import { EditTurbineDialog } from "./modal-edit/EditTurbineDialog.js";
import { ParkCell } from "./list/ParkCell.js";
import { TypeCell } from "./list/TypeCell.js";
import type { SiteOption } from "./shared/SiteAutocomplete.js";
import { PencilIcon, PlusIcon, WindIcon } from "@aeriosiq/ui/icons";
import { SlotChip } from "../intake/decide/SlotChip.js";

/** DI-3-S38 (item 5) — the search box's helper text: what `search` matches. */
const TURBINE_SEARCH_HELP = "code, park number, serial, model, site";
const TURBINE_SEARCH_HELP_ID = "turbines-search-help";

const COLUMNS_STORAGE_KEY = "aero.turbines.columns";

const TURBINE_COLUMNS_META: ColumnDescriptor[] = [
  { field: "assetCode", headerName: "Turbine Code" },
  { field: "identifier", headerName: "Identifier" },
  { field: "internalParkName", headerName: "Internal Park Name" },
  { field: "type", headerName: "Model" },
  { field: "park", headerName: "Park" },
  { field: "edit", headerName: "Edit", pinned: true },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

type SortKey =
  | "assetCode"
  | "internalParkName"
  | "identifier"
  | "type"
  | "park"
  | "createdAt";
type SortDir = "asc" | "desc";

// BE-supported sort keys.
// - `assets/v1@1.1.0` shipped `assetCode`, `internalParkName` (wire
//   name: `name`), `type`, `park`, `createdAt`.
// - `assets/v1@2.0.0` (FEAT-010, 2026-05-16) added
//   `identifier:asc/desc`; the BE expression is
//   `COALESCE(assets.identifier, '')` so NULL identifiers collate
//   before any real value.
// `internalParkName` is the FE column id; we translate to the BE wire
// name `name` in `listTurbinesUrl` (`hooks.ts`).
const SORTABLE_FIELDS: ReadonlySet<string> = new Set([
  "assetCode",
  "internalParkName",
  "identifier",
  "type",
  "park",
]);

function parseSort(
  value: string | null,
): { key: SortKey; dir: SortDir } | null {
  if (!value) return null;
  const [k, d] = value.split(":");
  if (!k || !d) return null;
  if (!SORTABLE_FIELDS.has(k)) return null;
  if (d !== "asc" && d !== "desc") return null;
  return { key: k as SortKey, dir: d };
}

function parsePageSize(value: string | null): number {
  const n = Number(value ?? "");
  if (
    !Number.isFinite(n) ||
    !PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])
  ) {
    return DEFAULT_PAGE_SIZE;
  }
  return n;
}

function parseCsv(value: string | null): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  for (const raw of value.split(",")) {
    const v = raw.trim();
    if (v.length > 0) seen.add(v);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

export function TurbinesPage(): ReactElement {
  return (
    <ToastProvider>
      <TurbinesPageInner />
    </ToastProvider>
  );
}

function TurbinesPageInner(): ReactElement {
  const [params, setParams] = useSearchParams();
  const turbineIdInUrl = params.get("turbine");
  const isNewOpen = params.get("new") === "1";
  const includeInactive = params.get("includeInactive") === "1";
  // Park-cell drill-down (AC24) — when present, opens the Story 054
  // Site detail modal in-place over the Turbines list. Composes with
  // every other URL-state param (search/sort/filter etc.).
  const siteIdInUrl = params.get("site"); // careful: also used as filter
  // Type-cell drill-down (2026-05-14 follow-up) — opens the Story 059
  // Edit/View Turbine Model modal in-place over the Turbines list,
  // mirroring the Park-cell pattern. `?model=<uuid>` is not overloaded
  // with any filter param, so the resolution is direct.
  const modelIdInUrl = params.get("model");

  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");
  const debouncedSearch = useDebounced(searchInput, 500);

  const [density, setDensity] = useState<Density>("compact");

  // SB2-CC-04 — Add button is hidden entirely when the matrix denies
  // `turbine.create`. Roles whose cell is `"conditional"` are resolved
  // optimistically via `useCanEditResourceFor`; the BE remains the
  // security boundary.
  const canAddTurbine = useCanEditResourceFor("turbine.create");

  // See SitesPage for the rationale — react-router v7 setSearchParams
  // is reference-unstable on URL change; without this ref guard the
  // search-mirror effect re-fires on every navigation and strips the
  // page param via `next.delete("page")`.
  const lastAppliedSearchRef = useRef(params.get("search") ?? "");

  // Mirror search into URL on debounced change.
  useEffect(() => {
    if (lastAppliedSearchRef.current === debouncedSearch) return;
    lastAppliedSearchRef.current = debouncedSearch;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch.length > 0) {
          next.set("search", debouncedSearch);
        } else {
          next.delete("search");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setParams]);

  const sortParsed = parseSort(params.get("sort"));
  const pageSize = parsePageSize(params.get("pageSize"));
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);

  // Type filter — CSV of asset_model UUIDs.
  const typeParam = params.get("type");
  const typeSelected = useMemo(() => parseCsv(typeParam), [typeParam]);

  // The Park `?site=<csv>` param is overloaded with the AC24
  // single-UUID drill-down param. We resolve them together: when the
  // value is a single UUID that matches a known site AND the value
  // didn't come from a multi-pick (CSV with comma), the AC24 modal
  // opens. The FE keeps the filter slot separate by using an
  // alternative URL key for the multi-pick filter — but to satisfy
  // both AC7 (`?site=<id1>,<id2>` for filter) and AC24
  // (`?site=<id>` for the drill-down) on the SAME param, we treat any
  // single-value (no comma) as the drill-down trigger AND as the
  // single-pick filter; multi-pick values (with comma) drive the
  // filter only and never open the dialog.
  const siteFilterCsv = useMemo(() => {
    if (!siteIdInUrl) return [];
    if (siteIdInUrl.includes(",")) {
      return parseCsv(siteIdInUrl);
    }
    // Single value — also drives the filter so the URL state stays
    // a single source of truth across both axes.
    return [siteIdInUrl];
  }, [siteIdInUrl]);

  // Drill-down site ID — set via the Park-cell click (AC24). The
  // URL's `?site=<single>` param is overloaded with the Park filter,
  // so the drill-down modal opens only when the user explicitly
  // clicked a Park cell in this session (and the URL still reflects
  // that click). A direct-load of `/turbines?site=<single-uuid>`
  // (AC8) is interpreted as a Park-filter pick, NOT a drill-down.
  const [drillSiteId, setDrillSiteId] = useState<string | null>(null);
  const drillSiteRef = useRef<string | null>(null);
  // When the URL drops `site` (e.g. user closed the modal via Escape),
  // also clear the drill-down state.
  useEffect(() => {
    if (siteIdInUrl === null && drillSiteRef.current !== null) {
      drillSiteRef.current = null;
      setDrillSiteId(null);
    }
  }, [siteIdInUrl]);

  const queryParams = useMemo(() => {
    const out: {
      page: number;
      pageSize: number;
      search?: string;
      sort?: string;
      includeInactive?: boolean;
      type?: readonly string[];
      site?: readonly string[];
    } = { page, pageSize };
    if (debouncedSearch.length > 0) out.search = debouncedSearch;
    if (sortParsed) out.sort = `${sortParsed.key}:${sortParsed.dir}`;
    if (includeInactive) out.includeInactive = true;
    if (typeSelected.length > 0) out.type = typeSelected;
    if (siteFilterCsv.length > 0) out.site = siteFilterCsv;
    return out;
  }, [
    page,
    pageSize,
    debouncedSearch,
    sortParsed,
    includeInactive,
    typeSelected,
    siteFilterCsv,
  ]);

  const { data, isPending, isError } = useTurbinesListQuery(queryParams);

  // Sites for the Add/Edit Turbine modal Park picker (`siteOptions`
  // prop). Modal-only consumer, opened on explicit user action —
  // 5-minute cache absorbs the multi-page fetch cost. The hot-path
  // Park-filter dropdown does NOT use this; it runs a server-side
  // typeahead via `useSitesSearchQuery` further down so we never
  // download every site on page load. FEAT-010 files a
  // `?compact=true` BE follow-up that collapses both paths into a
  // single roundtrip.
  const { data: sitesData } = useAllSitesQuery();
  const siteOptions = useMemo<SiteOption[]>(
    () =>
      (sitesData?.items ?? []).map((s) => ({
        id: s.id,
        siteName: s.siteName,
      })),
    [sitesData?.items],
  );

  // Asset-model options for the Type filter. Reuses spec 062 with no
  // assetTypeId — returns ALL permitted models. The catalogue is
  // small (~60 entries) and cached 5min, so `<TypeaheadChipFilter>`
  // filters client-side.
  const { data: allModelsData } = useAssetModelsForTypeQuery(null);
  const typeOptions = useMemo(
    () => allModelsData?.items ?? [],
    [allModelsData?.items],
  );

  // Park filter — `<AsyncTypeaheadChipFilter>` lives at the toolbar
  // so the parent owns the debounced search hook. `parkLabelHint`
  // seeds the chip labels for selected ids that aren't in the
  // current server hits (e.g. when restoring `?site=<id>` from URL
  // state before the typeahead has fired).
  const [parkInput, setParkInput] = useState("");
  const debouncedParkInput = useDebounced(parkInput, 300);
  const { data: parkSearchData, isFetching: parkSearchLoading } =
    useSitesSearchQuery(debouncedParkInput);
  const parkSearchOptions = useMemo(
    () => parkSearchData?.items ?? [],
    [parkSearchData?.items],
  );
  const parkLabelHint = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of siteOptions) map.set(s.id, s.siteName);
    return map;
  }, [siteOptions]);

  // Column visibility persisted to localStorage.
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, boolean>;
      }
      return {};
    } catch {
      return {};
    }
  });

  const handleColumnVisibilityChange = useCallback(
    (next: Record<string, boolean>) => {
      setColumnVisibility(next);
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(
            COLUMNS_STORAGE_KEY,
            JSON.stringify(next),
          );
        } catch {
          // Best-effort.
        }
      }
    },
    [],
  );

  function openTurbine(id: string): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("turbine", id);
        return next;
      },
      { replace: false },
    );
  }
  function closeTurbine(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("turbine");
        return next;
      },
      { replace: false },
    );
  }
  function openNew(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("new", "1");
        return next;
      },
      { replace: false },
    );
  }
  function closeNew(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("new");
        return next;
      },
      { replace: false },
    );
  }

  function openSiteDrillDown(siteId: string): void {
    drillSiteRef.current = siteId;
    setDrillSiteId(siteId);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("site", siteId);
        return next;
      },
      { replace: false },
    );
  }
  function closeSiteDrillDown(): void {
    drillSiteRef.current = null;
    setDrillSiteId(null);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("site");
        return next;
      },
      { replace: false },
    );
  }

  function openModelDrillDown(modelId: string): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("model", modelId);
        return next;
      },
      { replace: false },
    );
  }
  function closeModelDrillDown(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("model");
        return next;
      },
      { replace: false },
    );
  }

  function setTypeSelected(values: readonly string[]): void {
    const canonical = [...new Set(values.map((c) => c.trim()))]
      .filter((c) => c.length > 0)
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) {
          next.delete("type");
        } else {
          next.set("type", canonical.join(","));
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  function setSiteSelected(values: readonly string[]): void {
    const canonical = [...new Set(values.map((c) => c.trim()))]
      .filter((c) => c.length > 0)
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) {
          next.delete("site");
        } else {
          next.set("site", canonical.join(","));
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  function toggleIncludeInactive(checked: boolean): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (checked) {
          next.set("includeInactive", "1");
        } else {
          next.delete("includeInactive");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setPage(p: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (p <= 1) {
          next.delete("page");
        } else {
          next.set("page", String(p));
        }
        return next;
      },
      { replace: true },
    );
  }
  function setPageSize(n: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (n === DEFAULT_PAGE_SIZE) {
          next.delete("pageSize");
        } else {
          next.set("pageSize", String(n));
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  const total = data?.total ?? 0;

  const sortModel = useMemo<GridSortModel>(() => {
    if (!sortParsed) return [];
    return [{ field: sortParsed.key, sort: sortParsed.dir }];
  }, [sortParsed]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const head = model[0];
      if (!head?.sort) {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("sort");
            return next;
          },
          { replace: true },
        );
        return;
      }
      if (!SORTABLE_FIELDS.has(head.field)) return;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sort", `${head.field}:${head.sort ?? "asc"}`);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const columns = useMemo<GridColDef<Turbine>[]>(
    () => [
      {
        field: "assetCode",
        headerName: "Turbine Code",
        sortable: true,
        width: 110,
        align: "right",
        headerAlign: "right",
        renderCell: (p) => {
          // Visually-hidden internalParkName prefix so AC2's
          // `.filter({ hasText: /^T-01/ })` regex anchored on the
          // row's textContent matches even though Turbine Code is
          // the first visible column. Mirrors the Sites trick.
          const tag =
            p.row.internalParkNumber !== null &&
            p.row.internalParkNumber.length > 0
              ? p.row.internalParkNumber
              : `#${String(p.row.assetCode)}`;
          return (
            <Box
              sx={{
                fontVariantNumeric: "tabular-nums",
                width: "100%",
                textAlign: "left",
              }}
            >
              <Box
                component="span"
                sx={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                  clip: "rect(0 0 0 0)",
                  whiteSpace: "nowrap",
                }}
              >
                {tag}{" "}
              </Box>
              {p.row.assetCode}
            </Box>
          );
        },
      },
      {
        field: "identifier",
        headerName: "Identifier",
        // SB2-W-11 (2026-05-20) — turned on. `assets/v1@2.0.0`
        // (FEAT-010, 2026-05-16) added `identifier:asc/desc` to the
        // listTurbines sort enum; the BE expression is
        // `COALESCE(assets.identifier, '')` so NULL identifiers
        // collate before any real value.
        sortable: true,
        flex: 1.0,
        minWidth: 140,
        renderCell: (p) => {
          // No `#<assetCode>` fallback — when null, render empty
          // (Christian's Q3 lock 2026-05-13).
          // DI-3-S38 (item 5) — a placeholder turbine (the intake's drop
          // minted it, `Turbine.provisional`) carries a neutral D81 chip
          const placeholder = p.row.provisional ? (
            <Box component="span" sx={{ ml: 1, display: "inline-flex" }}>
              <SlotChip
                slot="neutral"
                label="placeholder"
                testId="turbine-placeholder-chip"
              />
            </Box>
          ) : null;
          if (p.row.identifier === null || p.row.identifier.length === 0) {
            return placeholder ?? <span aria-hidden />;
          }
          return (
            <Stack direction="row" alignItems="center" spacing={0}>
              <Typography variant="body2" component="span">
                {p.row.identifier}
              </Typography>
              {placeholder}
            </Stack>
          );
        },
      },
      {
        field: "internalParkName",
        headerName: "Internal Park Name",
        sortable: true,
        flex: 1.1,
        minWidth: 160,
        renderCell: (p) => {
          const display =
            p.row.internalParkNumber !== null &&
            p.row.internalParkNumber.length > 0
              ? p.row.internalParkNumber
              : `#${String(p.row.assetCode)}`;
          return (
            <Stack direction="row" alignItems="center" spacing={0}>
              <Typography variant="body2" component="span">
                {display}
              </Typography>
              {!p.row.isActive ? <InactiveBadge /> : null}
            </Stack>
          );
        },
      },
      {
        // BE wire sort key is `type` (assets/v1@1.1.0 listTurbines
        // sort enum). FE displays it as "Model" — the values are
        // model names like "Vestas V90", not asset types.
        field: "type",
        headerName: "Model",
        sortable: true,
        flex: 1.2,
        minWidth: 180,
        renderCell: (p) => (
          <TypeCell
            modelName={p.row.modelName}
            onOpen={() => {
              openModelDrillDown(p.row.assetModelId);
            }}
          />
        ),
      },
      {
        field: "park",
        headerName: "Park",
        sortable: true,
        flex: 1.2,
        minWidth: 160,
        renderCell: (p) => (
          <ParkCell
            siteName={p.row.siteName}
            onOpen={() => {
              openSiteDrillDown(p.row.siteId);
            }}
          />
        ),
      },
      {
        field: "edit",
        headerName: "Edit",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 96,
        renderCell: (p) => (
          <Button
            size="small"
            variant="text"
            startIcon={<PencilIcon fontSize="small" />}
            onClick={(e) => {
              e.stopPropagation();
              openTurbine(p.row.id);
            }}
          >
            Edit
          </Button>
        ),
      },
    ],
    // openTurbine + openSiteDrillDown are stable closures over
    // setParams (which is a ref-stable react-router callback).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <DataPageShell title="Turbines">
      <DataPageToolbar
        comfortableFilters
        ariaLabel="Turbines toolbar"
        filtersTestId="turbines-toolbar-filters"
        settingsTestId="turbines-toolbar-settings"
        filters={
          <>
            <SearchField
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search"
              ariaLabel="Search"
              describedBy={TURBINE_SEARCH_HELP_ID}
            />
            <TypeaheadChipFilter
              label="Model"
              value={typeSelected}
              onChange={setTypeSelected}
              options={typeOptions}
              getOptionId={(m) => m.id}
              getOptionLabel={(m) => m.name}
            />
            <AsyncTypeaheadChipFilter
              label="Park"
              value={siteFilterCsv}
              onChange={setSiteSelected}
              options={parkSearchOptions}
              loading={parkSearchLoading}
              selectedHints={parkLabelHint}
              onInputChange={setParkInput}
              getOptionId={(s) => s.id}
              getOptionLabel={(s) => s.siteName}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeInactive}
                  onChange={(_e, c) => {
                    toggleIncludeInactive(c);
                  }}
                  inputProps={{
                    "aria-label": "Show inactive turbines",
                  }}
                />
              }
              label="Show inactive"
              sx={{ ml: 0, mr: 0 }}
            />
            {/* DI-3-S38 (item 5) — the search's helper text: what it
                matches, so the intake's "placeholder turbine 10755" is a
                documented way in (assets/v1 4.10.1 `search` matches the code) */}
            <Typography
              id={TURBINE_SEARCH_HELP_ID}
              variant="caption"
              color="text.secondary"
              data-testid="turbines-search-help"
              sx={{ flexBasis: "100%", mt: -1.5 }}
            >
              {TURBINE_SEARCH_HELP}
            </Typography>
          </>
        }
        settings={
          <>
            <ColumnVisibilityButton
              columns={TURBINE_COLUMNS_META}
              model={columnVisibility}
              onChange={handleColumnVisibilityChange}
            />
            <DensityToggleButton density={density} onChange={setDensity} />
            <Box sx={{ flex: 1 }} />
            {canAddTurbine ? (
              <Button
                aria-label="Add turbine"
                onClick={openNew}
                startIcon={<PlusIcon fontSize="small" />}
                disableElevation
                sx={(t) => {
                  const mode = t.palette.mode;
                  const tokens =
                    mode === "dark" ? sidebarTokens.dark : sidebarTokens.light;
                  return {
                    height: 32,
                    minHeight: 32,
                    paddingInline: 10,
                    borderRadius: 1.5,
                    fontSize: typography.size["body-md"],
                    fontWeight: typography.weight.medium,
                    letterSpacing: typography.tracking["body-md"],
                    lineHeight: 1,
                    bgcolor: tokens.selectedBg,
                    color: tokens.selectedFg,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: t.palette.primary.light,
                      boxShadow: "none",
                    },
                    "&:active": {
                      bgcolor: tokens.rowPressedBg,
                      color: tokens.selectedFg,
                      boxShadow: "none",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${t.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: 1,
                      marginLeft: -0.25,
                    },
                  };
                }}
              >
                Add turbine
              </Button>
            ) : null}
          </>
        }
      />

      {isPending ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error" sx={{ p: 2 }}>
          Couldn&apos;t load turbines.
        </Typography>
      ) : data.items.length === 0 ? (
        <TurbinesEmptyState onAdd={openNew} canAdd={canAddTurbine} />
      ) : (
        <DataTable<Turbine>
          rows={data.items}
          columns={columns}
          ariaLabel="Turbines"
          density={density}
          getRowId={(row) => row.id}
          rowCount={total}
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(m) => {
            const nextPage = m.page + 1;
            if (m.pageSize !== pageSize) {
              setPageSize(m.pageSize);
            } else if (nextPage !== page) {
              setPage(nextPage);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          columnVisibilityModel={columnVisibility}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          pinnedActionColumn
          getRowClassName={(p) => (!p.row.isActive ? "row--inactive" : "")}
          sx={{
            "& .row--inactive": { opacity: 0.55 },
          }}
        />
      )}

      <DataPageFooter
        total={total}
        entityLabel="turbines"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        totalTestId="turbines-footer-total"
      />

      <AddTurbineDialog
        open={isNewOpen}
        onClose={closeNew}
        siteOptions={siteOptions}
      />
      <EditTurbineDialog
        turbineId={turbineIdInUrl}
        onClose={closeTurbine}
        siteOptions={siteOptions}
      />
      <EditSiteDialog siteId={drillSiteId} onClose={closeSiteDrillDown} />
      <EditTurbineModelDialog
        modelId={modelIdInUrl}
        onClose={closeModelDrillDown}
      />
    </DataPageShell>
  );
}

function TurbinesEmptyState({
  onAdd,
  canAdd,
}: {
  onAdd: () => void;
  canAdd: boolean;
}): ReactElement {
  return (
    <Box
      sx={{
        flex: 1,
        py: 8,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <WindIcon sx={{ fontSize: 48, color: "text.secondary" }} aria-hidden />
      <Typography variant="h6" color="text.primary">
        No turbines yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Add the first turbine deployed at one of your sites.
      </Typography>
      {canAdd ? (
        <Button variant="contained" onClick={onAdd}>
          Add turbine
        </Button>
      ) : null}
    </Box>
  );
}

// The inline ParkFilterAutocomplete + TypeFilterAutocomplete that
// used to live here were promoted to `@aeriosiq/ui` as
// `<AsyncTypeaheadChipFilter>` and `<TypeaheadChipFilter>` — see
// `.vibe/60_TEMPLATE_DATA_PAGE.md` §"Sub-template 5 / typeahead-style
// filters" for the canonical pattern.
